import { guidFor } from '@ember/object/internals';
import { restartableTask, timeout } from 'ember-concurrency';
import { service } from '@ember/service';
import { task } from 'reactiveweb/ember-concurrency';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import RemoteSelect from 'tio-ui/components/remote-select';
import type EmployeeModel from '../../models/employee';
import type StoreService from '@ember-data/store';

const INPUT_DEBOUNCE = /* config.environment === 'test' ? 0 : */ 250;

export interface SelectEmployeeArgs {
  Args: {
    companyId: string;
    selected?: string | EmployeeModel | null;
    onChange: (value?: EmployeeModel) => void;
    label?: string;
    labelClass?: string;
    placeholder?: string;
    showPayrollId?: boolean;
    errors?: string[];
  };
  Element: HTMLDivElement;
}

export default class SelectEmployeeComponent extends Component<SelectEmployeeArgs> {
  @service declare store: StoreService;
  @tracked filterTerm = '';

  get uniqueId() {
    return guidFor(this);
  }

  get selectedId(): string | undefined {
    const selected = this.args.selected;
    if (typeof selected === 'string') {
      return selected;
    } else if (selected && selected.id) {
      return selected.id;
    }
    return undefined;
  }

  get selectedKey() {
    return this.selectedId || '';
  }

  fetchEmployees = restartableTask(async (term: string) => {
    await timeout(INPUT_DEBOUNCE);
    const results = await this.store.query<EmployeeModel>('employee', {
      filter: {
        search: term,
        company: this.args.companyId,
      },
    });

    return results
      .filter((employee) => !!employee.id)
      .map((employee) => ({
        key: employee.id!,
        label: this.args.showPayrollId
          ? `${employee.fullName} – ID # ${employee.payrollId}`
          : employee.fullName,
      }));
  });

  fetchEmployeesTask = task(this, this.fetchEmployees, () => [this.filterTerm]);

  get items() {
    let list = (this.fetchEmployeesTask.value ?? []) as { key: string; label: string }[];

    if (this.selectedId && !list.find((item) => item.key === this.selectedId)) {
      const selected = this.args.selected as EmployeeModel;
      const label =
        typeof selected === 'object'
          ? this.args.showPayrollId
            ? `${selected.fullName} – ID # ${selected.payrollId}`
            : selected.fullName
          : '';

      list = [{ key: this.selectedId, label }, ...list];
    }

    return list;
  }

  onFilterChange = (event: Event) => {
    this.filterTerm = (event.target as HTMLInputElement).value;
  };

  handleSelectionChange = async (key: string | null) => {
    if (!key) {
      this.filterTerm = ''; // When cleared, reset the filter term
      this.args.onChange();
    } else {
      // re-fetch the full model so parent gets the actual EmployeeModel
      const record = await this.store.findRecord<EmployeeModel>('employee', key);
      this.args.onChange(record);
    }
  };
  <template>
    <div ...attributes>
      <label for={{this.uniqueId}} class="text-foreground {{@labelClass}}">
        {{@label}}
      </label>

      <RemoteSelect
        @id={{this.uniqueId}}
        @name="employee"
        @placeholder={{@placeholder}}
        @isFilterable={{true}}
        @isLoading={{this.fetchEmployeesTask.isRunning}}
        @items={{this.items}}
        @selectedKey={{this.selectedKey}}
        {{! @glint-expect-error - RemoteSelect enhanced component type definitions }}
        @onFilterChange={{this.onFilterChange}}
        @onSelectionChange={{this.handleSelectionChange}}
        @isClearable={{true}}
        @errors={{@errors}}
        @selectionMode="single"
        class="truncate"
      />
    </div>
  </template>
}
